import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Checkbox, Grid, TextField, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import SimpleBackdrop from '../../Loaders/SimpleBackdrop'
import { getErrorMessage, inputHasError } from '../../../utils/formErrorUtils'
import FacebookLoginButton from '../../Base/FacebookLoginButton'
import GoogleLoginButton from '../../Base/GoogleLoginButton'
import Alert from '@material-ui/lab/Alert'
import Link from '@material-ui/core/Link'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'

const useStyles = makeStyles((theme) => ({
    hr: {
        color: '#8f8f8f',
    },
    socialLoginButtonContainer: {
        flexDirection: 'column',
        margin: '1rem 0 2.2rem 0',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
        },
    },
    facebookButton: {
        marginBottom: '1.3rem',
    },
    createAccountButton: {
        padding: '0.3rem',
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
    },
    termsText: {
        textAlign: 'justify',
        fontSize: '0.8rem',
        color: '#000000',
        margin: '1rem auto',
    },
    linkText: {
        textDecoration: 'underline',
        color: '#000000',
    },
    seperator: {
        marginBottom: '1rem',
    },
    seperatorText: {
        fontSize: '1rem',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    newsletterText: {
        fontSize: '0.8rem',
        color: '#616161',
    },
    loginButtonContainer: {
        marginTop: '1rem',
    },
    loginButton: {
        backgroundColor: 'white',
        color: '#000000',
        fontWeight: 'bold',
        textTransform: 'capitalize',
        fontSize: '0.8rem',
    },
    roundedBtn: {
        borderRadius: 50,
        borderWidth: 3,
        '&:hover': {
            borderRadius: 50,
            borderWidth: 3,
        },
    },
    greenBtn: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.secondary.main,
        '&:hover': {
            borderColor: theme.palette.secondary.main,
        },
    },
    checkBox: {
        '& svg': {
            fontSize: '2.25rem',
        },
    },
    greyRoundTextinput: {
        width: '100%',
        maxWidth: '100%',
        backgroundColor: '#F4F4F4',
        borderRadius: '60px',
        height: '42px',
        padding: '0 0 2rem 0',
        margin: '0 0 28px 0',
        '& fieldset': {
            borderRadius: '60px',
        },
        '& input': {
            padding: '11.5px 14px',
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: '60px',
        },
    },
}))

const ConsumerCheckoutCreateAccountModalContent = (props) => {
    const {
        facebookLoginUrl,
        googleLoginUrl,
        signupDetails,
        setSignupDetails,
        signupFormErrors,
        processing,
        errors,
        handleShowLogin,
        validateSignupField,
        handleUserSignup,
        baseUrl,
    } = props
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
        defaultMatches: true,
    })

    return (
        <Grid item>
            <TextField
                id={'full-name'}
                variant={'outlined'}
                fullWidth
                size={'small'}
                style={{
                    marginTop: '2rem',
                    marginBottom:
                        isMobile && inputHasError(signupFormErrors, 'name')
                            ? '3rem'
                            : '2rem',
                }}
                placeholder={'Full Name'}
                value={signupDetails.name}
                error={inputHasError(signupFormErrors, 'name')}
                helperText={getErrorMessage(signupFormErrors, 'name')}
                disabled={processing}
                className={classes.greyRoundTextinput}
                onChange={(e) => {
                    validateSignupField('name', e.target.value)
                    setSignupDetails({
                        ...signupDetails,
                        name: e.target.value,
                    })
                }}
            />
            <TextField
                id={'email-address'}
                variant={'outlined'}
                fullWidth
                size={'small'}
                style={{
                    marginBottom:
                        isMobile && inputHasError(signupFormErrors, 'email')
                            ? '3rem'
                            : '2rem',
                }}
                placeholder={'Email address'}
                value={signupDetails.email}
                error={inputHasError(signupFormErrors, 'email')}
                helperText={getErrorMessage(signupFormErrors, 'email')}
                disabled={processing}
                className={classes.greyRoundTextinput}
                onChange={(e) => {
                    const email = e.target.value.trim()
                    validateSignupField('email', email)
                    setSignupDetails({
                        ...signupDetails,
                        email,
                    })
                }}
            />
            <TextField
                id={'password'}
                variant={'outlined'}
                fullWidth
                size={'small'}
                style={{
                    marginBottom:
                        isMobile && inputHasError(signupFormErrors, 'password')
                            ? '3.5rem'
                            : '2rem',
                }}
                placeholder={'Password'}
                type={'password'}
                value={signupDetails.password}
                error={inputHasError(signupFormErrors, 'password')}
                helperText={getErrorMessage(signupFormErrors, 'password')}
                disabled={processing}
                className={classes.greyRoundTextinput}
                onChange={(e) => {
                    validateSignupField('password', e.target.value)
                    setSignupDetails({
                        ...signupDetails,
                        password: e.target.value,
                    })
                }}
            />
            <Button
                id="create-account-button"
                variant="outlined"
                fullWidth
                color="primary"
                size="large"
                className={`${classes.roundedBtn} ${classes.greenBtn} ${classes.createAccountButton}`}
                disabled={processing}
                onClick={() => {
                    handleUserSignup(null)
                }}
            >
                Create Account{' '}
                <LockOutlinedIcon className={classes.buttonIcon} />
            </Button>
            {errors.length > 0 && (
                <Alert severity="error" style={{ marginTop: '1rem' }}>
                    This email address is reserved by a registered account. You
                    can request a{' '}
                    <Link
                        href={`${baseUrl}/secure/forgot-my-password/`}
                        target="_blank"
                        style={{ textDecoration: 'underline' }}
                    >
                        password reset
                    </Link>{' '}
                    to attempt recovery of this account. For assistance, please
                    contact our{' '}
                    <Link
                        href={`${baseUrl}/contact-us/`}
                        target="_blank"
                        style={{ textDecoration: 'underline' }}
                    >
                        customer service team
                    </Link>
                    .
                </Alert>
            )}
            <Typography gutterBottom className={classes.termsText}>
                By creating an account, you agree to Gift Card Granny's{' '}
                <a
                    className={classes.linkText}
                    href={'https://policies.google.com/privacy'}
                    target="_blank"
                >
                    Terms of Use
                </a>
                . This site is protected by reCAPTCHA and the Google{' '}
                <a
                    className={classes.linkText}
                    href={'https://policies.google.com/privacy'}
                    target="_blank"
                >
                    Privacy Policy
                </a>{' '}
                and{' '}
                <a
                    className={classes.linkText}
                    href={'https://policies.google.com/terms'}
                    target="_blank"
                >
                    Terms of Service
                </a>{' '}
                apply.
            </Typography>
            <Box
                className={classes.seperator}
                display={'flex'}
                alignItems={'center'}
            >
                <hr
                    className={classes.hr}
                    style={{ flex: 1, marginRight: '1rem' }}
                />
                <Typography className={classes.seperatorText}>
                    Or sign in with
                    <br /> social networks
                </Typography>
                <hr
                    className={classes.hr}
                    style={{ flex: 1, marginLeft: '1rem' }}
                />
            </Box>
            <SimpleBackdrop open={processing} />
            <Grid
                container
                spacing={2}
                className={classes.socialLoginButtonContainer}
            >
                <Grid item sm={12} md={6}>
                    <FacebookLoginButton
                        facebookLoginUrl={facebookLoginUrl}
                        classNameOverride={`${classes.facebookButton} ${classes.roundedBtn}`}
                    />
                </Grid>
                <Grid item sm={12} md={6}>
                    <GoogleLoginButton
                        googleLoginUrl={googleLoginUrl}
                        classNameOverride={`${classes.roundedBtn}`}
                    />
                </Grid>
            </Grid>
        </Grid>
    )
}

export default ConsumerCheckoutCreateAccountModalContent
